"use client";

/** Recovers render and query failures through a shared retry boundary. */

import { Button } from "@decibeltrade/web-core/components";
import { QueryErrorResetBoundary } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { PropsWithChildren } from "react";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { center } from "styled-system/patterns";

import { getIsFetchError } from "@/utils/error";
import { Logger } from "@/utils/logger";

export function GlobalErrorHandler({ children }: PropsWithChildren) {
  return (
    <QueryErrorResetBoundary>
      {({ reset }) => (
        <ErrorBoundary
          onReset={reset}
          onError={(error) => {
            // Capture boundary errors in Sentry
            Logger.error(error, {
              message: "Global error boundary error",
            });
          }}
          FallbackComponent={ErrorFallback}
        >
          {children}
        </ErrorBoundary>
      )}
    </QueryErrorResetBoundary>
  );
}

// TODO: Make the error state prettier later
function ErrorFallback({ resetErrorBoundary, error }: FallbackProps) {
  const t = useTranslations("general");
  const isFetchError = getIsFetchError(error);

  return (
    <div
      data-testid="error-boundary"
      className={center({
        flexDirection: "column",
        alignItems: "center",
        gap: "16",
        h: "[100vh]",
      })}
    >
      {isFetchError ? t("fetchError") : t("unexpectedError")}
      <Button onClick={() => resetErrorBoundary()}>{t("tryAgain")}</Button>
    </div>
  );
}
